import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import SEOHead from '../components/common/SEOHead';
import { Capacitor } from '@capacitor/core';
import { useAppSelector } from '../hooks/redux';

const DataDeletionStatusPage: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || 'del_request_processed';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEOHead
        title="Data Deletion Status - ClickCollab"
        description="Check the status of your Meta / Instagram data deletion request on ClickCollab."
      />
      <div className="sm:mx-auto sm:w-full sm:max-max-w-md max-w-md mx-auto w-full px-4">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <ShieldCheckIcon className="w-8 h-8" />
          </div>
        </div>
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Data Deletion Request Status
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          ClickCollab Meta Integration Compliance
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow rounded-2xl sm:px-10 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center space-x-3 mb-6 p-4 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-800">
            <CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                Data Deletion Processed
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                All associated access tokens and Instagram profile data have been deleted.
              </p>
            </div>
          </div>

          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 block mb-1">
                Confirmation Code
              </span>
              <code className="block bg-gray-100 dark:bg-gray-900 p-2.5 rounded-lg text-xs font-mono text-indigo-600 dark:text-indigo-400 break-all select-all">
                {code}
              </code>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 block mb-1">
                Details
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                In compliance with Meta Platform Policy, your authentication tokens, cached media listings, and user integration records have been removed from our databases.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center text-xs">
            <Link
              to="/privacy-policy"
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Privacy Policy
            </Link>
            <Link
              to={Capacitor.isNativePlatform() ? (isAuthenticated ? "/dashboard" : "/login") : "/"}
              className="text-gray-500 dark:text-gray-400 hover:underline"
            >
              {Capacitor.isNativePlatform() ? (isAuthenticated ? "Return to Dashboard" : "Return to Login") : "Return Home"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionStatusPage;
